import {
  FilterValueMetaType,
  LayoutNode,
  LayoutNodeActionType,
  LayoutNodeFilterType,
  LayoutNodeType,
} from "@towersystems/roam-common/lib/generated-types";
import { useContext } from "react";
import { Point } from "../drag-drop";
import {
  LayoutScreens,
  MAX_LAYOUT_ROWS,
  quickKeyTypesWithForm,
} from "./constants";
import { CreateTileContext, EditTileContext } from "./context";
import { LocalError, RemoteError } from "./errors";
import {
  ItemConfigType,
  LayoutDimensions,
  PageItemType,
  PageType,
  Promixity,
  Rect,
  TypeListItem,
} from "./types";

export function useCreateTile() {
  const context = useContext(CreateTileContext);
  if (!context) {
    throw new Error("No CreateTileContext was provided.");
  }
  return context;
}

export function useEditTile() {
  const context = useContext(EditTileContext);
  if (!context) {
    throw new Error("No EditTileContext context was provided.");
  }
  return context;
}

export function deriveLayoutDimensions(viewWidth: number): LayoutDimensions {
  const rows = MAX_LAYOUT_ROWS;
  let columns = 4;

  if (viewWidth < 700) {
    columns = 2;
  } else if (viewWidth <= 1000) {
    columns = 3;
  } else {
    columns = 4;
  }

  return {
    rows,
    columns,
    itemsPerPage: rows * columns,
    width: viewWidth,
  };
}

export function getClosestAxisAlignedRect(point: Point, rects: Rect[] = []) {
  let closestRect: Promixity<Rect> | undefined = undefined;

  for (const rect of rects) {
    const minX = rect.x;
    const maxX = rect.width + rect.x;
    const minY = rect.y;
    const maxY = rect.height + rect.y;

    let nearest = { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY };

    if (point.x < minX) nearest.x = minX;
    else if (point.x > maxX) nearest.x = maxX;

    if (point.y < minY) nearest.y = minY;
    else if (point.y > maxY) nearest.y = maxY;

    const distance = Math.sqrt(
      Math.pow(point.x - nearest.x, 2) + Math.pow(point.y - nearest.y, 2)
    );

    if (!closestRect || distance < closestRect?.distance) {
      closestRect = {
        distance,
        payload: rect,
      };
    }
  }

  return closestRect?.payload;
}

export function inRect({ x, y }: Point, rect: Rect) {
  const result =
    Math.round(rect.x) < x &&
    Math.round(rect.width + rect.x) > x &&
    Math.round(rect.y) < y &&
    Math.round(rect.height + rect.y) > y;

  if (result) {
    //console.log("A", Math.round(rect.x) < x, rect.x, x);
    //console.log("B", Math.round(rect.width + rect.x) > x, rect.width, x);
    //console.log("C", Math.round(rect.y) < y, rect.y, y);
    //console.log("D", Math.round(rect.height + rect.y) > y, rect.height, y);
    //console.log("in rect");
  }

  return result;
}

export function range(start: number, end: number) {
  return Array.from({ length: end - start }, (_, i) => i + start);
}

export enum Direction {
  Forward = "forward",
  Backward = "backward",
}

export type ComputeShiftDeltaValueParams = {
  fromValue?: Point;
  toValue?: Point;
  direction: Direction;
  componentWidth: number;
  numColumns: number;
};

export function extrapolateShiftCoords(
  coords: Point,
  direction: Direction,
  componentWidth: number
) {
  const isForward = direction === Direction.Forward;

  if (isForward) {
    return {
      x: coords.x - 2 * componentWidth,
      y: coords.y,
    };
  } else {
    return {
      x: coords.x + 2 * componentWidth,
      y: coords.y,
    };
  }
}

export function extrapolateRestoreCoords(coords: Point, direction: Direction) {
  const isForward = direction === Direction.Forward;

  if (isForward) {
    return {
      ...coords,
      x: coords.x,
      y: coords.y,
    };
  } else {
    return {
      ...coords,
      x: coords.x,
      y: coords.y,
    };
  }
}

/**
 * Computes the delta value for a shift animation.
 * Extrapolation is performed where a fromValue or toValue is missing
 * to allow for an item to be shifted out of view.
 */
export function computeShiftDeltaValue({
  fromValue,
  toValue,
  direction,
  componentWidth,
  numColumns,
}: ComputeShiftDeltaValueParams): Point {
  const deltaValue = {
    x: 0,
    y: 0,
  };

  if (fromValue == toValue) {
    return deltaValue;
  }

  if (!fromValue && !toValue) {
    throw Error("fromValue and toValue cannot both be undefined");
  }

  deltaValue.y = (toValue?.y || 0) - (fromValue?.y || 0);

  if (!toValue) {
    if (!fromValue) return deltaValue;
    toValue = extrapolateShiftCoords(fromValue, direction, componentWidth);
    deltaValue.y = 0;
  }

  if (!fromValue) {
    if (!toValue) return deltaValue;
    fromValue = extrapolateShiftCoords(toValue, direction, componentWidth);
    deltaValue.y = 0;
  }

  switch (direction) {
    case Direction.Forward:
      if (toValue.x < fromValue.x) {
        deltaValue.x = toValue.x - fromValue.x;
      } else {
        deltaValue.x = toValue.x - fromValue.x;
      }
      break;
    case Direction.Backward:
      if (toValue.x < fromValue.x) {
        deltaValue.x = toValue.x - fromValue.x;
      } else {
        deltaValue.x = toValue.x - fromValue.x;
      }
      break;
  }

  return deltaValue;
}

export async function attemptLocalAction<T>(action: () => Promise<T>) {
  try {
    const res = await action();
    return res;
  } catch (error: any) {
    throw new LocalError(error?.message);
  }
}

export async function attemptRemoteAction<T>(action: () => Promise<T>) {
  try {
    const res = await action();
    return res;
  } catch (error: any) {
    throw new RemoteError(error?.message);
  }
}

export function makePageItem(layoutNode: LayoutNode): PageItemType {
  return {
    id: layoutNode.id,
    position: layoutNode.position,
    placeholder: false,
    config: toItemConfigType(layoutNode) as PageItemType["config"],
  };
}

export function makePlaceholderPageItem(position: number) {
  return {
    id: `placeholder-${position}`,
    position,
    placeholder: true,
  };
}

export function getMaxTilePositionForPage(page: PageType) {
  if (!page.items) return 0;
  const res = page.items.reduce((acc, item) => {
    if (item.placeholder === false) {
      return item.position > acc ? item.position : acc;
    }
    return acc;
  }, 0);

  return res;
}

export function getPageNumberByPosition(
  layoutDimensions: LayoutDimensions,
  position: number
) {
  return Math.floor(position / layoutDimensions.itemsPerPage) + 1;
}

export function quickKeyHasForm<
  T extends Partial<Pick<ItemConfigType, "action" | "type">>
>(config: T) {
  if (config.action) {
    return quickKeyTypesWithForm.includes(config.action);
  } else if (config.type) {
    return quickKeyTypesWithForm.includes(config.type as any);
  }
  return false;
}

export function itemToNodeType(item: ItemConfigType): LayoutNodeType {
  const typeListItem = itemToTypeListItem(item);
  return typeListItemToNodeType(typeListItem);
}

export function itemToTypeListItem(item: ItemConfigType): TypeListItem {
  if (item.productId) {
    return LayoutNodeType.PRODUCT;
  } else if (item.action) {
    return item.action;
  } else if (item.filter) {
    return item.filter.type;
  } else {
    throw new Error("Invalid item");
  }
}

export function typeListItemToNodeType(
  typeListItem: TypeListItem
): LayoutNodeType {
  if (isActionNode(typeListItem)) {
    return LayoutNodeType.ACTION;
  } else if (isFilterNode(typeListItem)) {
    return LayoutNodeType.FILTER;
  } else if (isProductNode(typeListItem)) {
    return LayoutNodeType.PRODUCT;
  }

  throw new Error("Invalid typeListItem");
}

function isActionNode(
  typeListItem: TypeListItem
): typeListItem is LayoutNodeActionType {
  return Object.values(LayoutNodeActionType).includes(
    typeListItem as unknown as any
  );
}

function isFilterNode(
  typeListItem: TypeListItem
): typeListItem is LayoutNodeFilterType {
  return Object.values(LayoutNodeFilterType).includes(
    typeListItem as unknown as any
  );
}

function isProductNode(
  typeListItem: TypeListItem
): typeListItem is LayoutNodeType {
  return typeListItem === LayoutNodeType.PRODUCT;
}

export function typeListItemToCreateNavigationArgs(typeListItem: TypeListItem) {
  const nodeType = typeListItemToNodeType(typeListItem);

  switch (nodeType) {
    case LayoutNodeType.PRODUCT:
      return {
        screen: LayoutScreens.CreateProductQuickKeyRootScreen,
      };
    case LayoutNodeType.ACTION: {
      if (typeListItem === LayoutNodeActionType.CUSTOM_URL) {
        return { screen: LayoutScreens.CreateCustomUrlQuickKeyScreen };
      }
      throw Error("No screen found for action type: " + typeListItem);
    }
    case LayoutNodeType.FILTER: {
      return {
        screen: LayoutScreens.CreateFilterQuickKeyRootScreen,
        params: { filter: typeListItem },
      };
    }
  }
}

export function toItemConfigType(layoutNode: LayoutNode) {
  return {
    id: layoutNode.id,
    layoutId: layoutNode.layoutId,
    productId: layoutNode.productId,
    label: layoutNode.label,
    color: layoutNode.color,
    showImage: layoutNode.showImage,
    type: layoutNode.type,
    action: layoutNode.action,
    link: layoutNode.link,
    position: layoutNode.position,
    filter: layoutNode.filter,
    imageUrl: layoutNode.imageUrl,
    labelColor: layoutNode.labelColor,
  };
}

export function filterTypeToFilterValueMetaType(
  layoutNodeFilterType: LayoutNodeFilterType
) {
  const res = filterTypeToFilterValueMetaTypeMap[layoutNodeFilterType];

  if (!res) throw new Error(`Invalid filter type: ${layoutNodeFilterType}`);

  return res;
}

const filterTypeToFilterValueMetaTypeMap = {
  [LayoutNodeFilterType.CATEGORY]: FilterValueMetaType.CATEGORY_ID,
  [LayoutNodeFilterType.DEPARTMENT]: FilterValueMetaType.DEPARTMENT_ID,
};
